import React from 'react';
import { TaskOverlay, Textarea, Alert, Button, Spinner } from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import { useDirtyClose } from './common/DirtyCloseGuard';

// ─────────────────────────────────────────────────────────────────────────────
// The overlays behind پذیرش's row ops (PM-0518), for both surfaces: the confirm
// for «لغو نوبت» and «عدم‌حضور», and the «یادداشت نوبت» form. State and writes
// are `useReceptionRowOps`'s; this renders them.
//
// «لغو نوبت» says it cannot be undone because it cannot: since PM-0458 the
// service deletes a cancelled نوبت, and «بازگشت به مطب» has nothing to reopen.
// ─────────────────────────────────────────────────────────────────────────────

export const NOTE_MAX = 500; // the clinic desk's own limit, which the BFF enforces too

function NoteOverlay({ apt, onClose, onSave, s, isMobile }) {
  const open = !!apt;
  const initial = apt?.reminderNote || '';
  const [text, setText] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setText(initial);
    setBusy(false);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apt?.id]);

  const changed = text.trim() !== initial.trim();
  const { requestClose, guard } = useDirtyClose(changed && !busy, onClose);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy || !changed) return;
    setBusy(true);
    setError('');
    try {
      await onSave(text.trim());
    } catch (err) {
      setError(err?.message || s('dashboard.rowOps.errors.note'));
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <Button variant="outline" type="button" onClick={requestClose} disabled={busy}>
        {s('dashboard.withdraw.keep')}
      </Button>
      <Button
        type="submit"
        form="appointment-note-form"
        disabled={busy || !changed}
        aria-busy={busy || undefined}
        data-testid="appointment-note-save"
      >
        {busy && <Spinner size={16} color="currentColor" />}
        {s('dashboard.rowOps.noteSave')}
      </Button>
    </div>
  );

  return (
    <>
      <TaskOverlay
        open={open}
        onClose={requestClose}
        title={s('dashboard.rowOps.noteTitle')}
        footer={footer}
        busy={busy}
        size="sm"
        isMobile={isMobile}
        data-testid="appointment-note"
      >
        <form id="appointment-note-form" onSubmit={submit} noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && <Alert variant="error" data-testid="appointment-note-error">{error}</Alert>}
          <Textarea
            label={s('dashboard.rowOps.noteLabel', { name: apt?.name || '' })}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, NOTE_MAX))}
            maxLength={NOTE_MAX}
            showCount
            rows={4}
            disabled={busy}
            data-testid="appointment-note-text"
            autoFocus={!isMobile}
          />
          <div style={{ fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.8 }}>
            {s('dashboard.rowOps.noteHint')}
          </div>
        </form>
      </TaskOverlay>
      {guard}
    </>
  );
}

export default function ReceptionRowOpsDialogs({ rowOps, isMobile = false }) {
  const { s, nameOf } = rowOps;
  const cancelApt = rowOps.pendingCancel;
  const noShowApt = rowOps.pendingNoShow;

  return (
    <>
      <ConfirmDialog
        open={!!cancelApt}
        onOpenChange={(o) => { if (!o) rowOps.cancelCancel(); }}
        title={s('dashboard.rowOps.cancelTitle')}
        message={s('dashboard.rowOps.cancelMessage', { name: nameOf(cancelApt), time: cancelApt?.time || '' })}
        confirmLabel={s('dashboard.rowOps.cancelConfirm')}
        cancelLabel={s('dashboard.withdraw.keep')}
        destructive
        confirmTestId="row-op-cancel-confirm"
        onConfirm={rowOps.confirmCancel}
      />

      <ConfirmDialog
        open={!!noShowApt}
        onOpenChange={(o) => { if (!o) rowOps.cancelNoShow(); }}
        title={s('dashboard.rowOps.noShowTitle')}
        message={s('dashboard.rowOps.noShowMessage', { name: nameOf(noShowApt), time: noShowApt?.time || '' })}
        confirmLabel={s('dashboard.rowOps.noShowConfirm')}
        cancelLabel={s('dashboard.withdraw.keep')}
        destructive
        confirmTestId="row-op-noshow-confirm"
        onConfirm={rowOps.confirmNoShow}
      />

      <NoteOverlay
        apt={rowOps.noteFor}
        onClose={rowOps.closeNote}
        onSave={rowOps.saveNote}
        s={s}
        isMobile={isMobile}
      />
    </>
  );
}
